import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { BaseFilterProps, useFilterContext } from './FilterContext';
import { FilterCondition } from 'framework/types';
import { DateInput } from 'framework/form';
import { DEFAULT_DATE_TIME_FORMAT, DEFAULT_FORMAT } from '../../utils/hooks/useDateService';
import { SxProps } from '@mui/material';
import useSingleDateFilter from './useSingleDateFilter';
import { useUiTheme } from 'framework/uiTheme';
import { isEmpty } from 'lodash';
import { FloatingPlacement } from 'framework/ui/uiContract';

export interface SingleDateFilterProps extends BaseFilterProps {
  dateFormat?: string;
  dateTimeFormat?: string;
  canBeInFuture?: boolean;
  isRequired?: boolean;
  hasTodayButton?: boolean;
  todayButtonLabel?: string;
  label?: string;
  sx?: SxProps;
  popperPlacement?: FloatingPlacement;
  condition?: FilterCondition;
}

const SingleDateFilter = ({
  source,
  dateFormat = DEFAULT_FORMAT,
  dateTimeFormat = DEFAULT_DATE_TIME_FORMAT,
  canBeInFuture = false,
  label,
  isRequired = false,
  popperPlacement,
  condition = FilterCondition.EQ,
  ...props
}: SingleDateFilterProps) => {
  const { SingleDateFilterLayout } = useUiTheme();
  const { filterBy, getValue } = useFilterContext();
  const { validateAndShowErrorMessages, getErrors, fromApiToFrontFormat, getMaxDate, formatDateForAPI } =
    useSingleDateFilter(dateFormat, dateTimeFormat, canBeInFuture, source, condition);
  // filterValue stores text date in FE format
  const [filterValue, setFilterValue] = useState(fromApiToFrontFormat(getValue(source, condition)));
  const MAX_DATE = getMaxDate();

  const hasError = !isEmpty([...getErrors(condition)]);
  const invalidClass = hasError ? 'is-invalid' : '';

  /**
   * @param filterPair - object like {"eq": "2020-08-05"} means equal
   */
  const onChangeCallback = (filterPair: Dictionary<string>) => {
    const newFilterValue = filterPair;

    if (filterPair) {
      const inputName = Object.keys(filterPair)[0] as FilterCondition;
      const textDate = filterPair[inputName];
      const isInvalid = validateAndShowErrorMessages(inputName, textDate);

      if (!isInvalid && textDate) {
        newFilterValue[inputName] = formatDateForAPI(textDate);
      }

      setFilterValue(!isInvalid && textDate ? formatDateForAPI(textDate) : '');
      filterBy({
        [source]: newFilterValue,
      });
    }
  };

  /*
   * Initial validation (for example needed when url contains filter object)
   */
  useEffect(() => {
    if (filterValue) {
      validateAndShowErrorMessages(condition, fromApiToFrontFormat(filterValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatSourceDate = (textDate: string) => {
    const formattedDate = moment(textDate, dateFormat, true);

    if (formattedDate.isValid()) {
      return formattedDate.format(dateFormat);
    }
    return textDate;
  };

  return (
    <SingleDateFilterLayout
      label={label}
      dateValue={fromApiToFrontFormat(filterValue)}
      onChangeCallback={onChangeCallback}
      source={source}
      className={invalidClass}
      popperPlacement={popperPlacement}
      condition={condition}
      dateFormat={dateFormat}
    >
      <DateInput
        {...props}
        label={label}
        source={condition}
        onChangeCallback={onChangeCallback}
        value={fromApiToFrontFormat(filterValue)}
        dateFormat={dateFormat}
        maxDate={MAX_DATE}
        errorMessages={getErrors(condition)}
        formatSourceDate={formatSourceDate}
      />
    </SingleDateFilterLayout>
  );
};

export default SingleDateFilter;
